import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

const defaultDdi = "55";

const sanitizeNumber = (value) => (value || "").replace(/\D/g, "");
const sanitizeDdiInput = (value) => {
  const raw = (value || "").replace(/\D/g, "");
  return raw.slice(0, 3);
};
const sanitizePhoneInput = (value, maxLength = 15) =>
  (value || "").replace(/\D/g, "").slice(0, maxLength);
const buildPhoneValue = (ddiDigits, phoneDigits, fallbackDdi = defaultDdi) =>
  phoneDigits ? `${ddiDigits || fallbackDdi}${phoneDigits}` : "";

const getPhoneLengthError = (ddiDigits, numberDigits) => {
  if (!numberDigits) return "";

  const isBrazil = ddiDigits === "55";
  const hasDdi = Boolean(ddiDigits);

  if (isBrazil) {
    if (numberDigits.length !== 10 && numberDigits.length !== 11) {
      return "Para DDI 55, use 10 ou 11 digitos (total 12 ou 13)";
    }
    return "";
  }

  if (!hasDdi) {
    if (numberDigits.length !== 10 && numberDigits.length !== 11) {
      return "Sem DDI, use 10 ou 11 digitos";
    }
    return "";
  }

  const totalDigits = ddiDigits.length + numberDigits.length;
  if (totalDigits < 8 || totalDigits > 15) {
    return "Para DDI diferente de 55, use entre 8 e 15 digitos no total";
  }
  return "";
};

const getPhoneMaxDigits = (ddiDigits) => {
  const hasDdi = Boolean(ddiDigits);
  const isBrazil = ddiDigits === "55";

  if (isBrazil || !hasDdi) {
    return 11;
  }

  const maxByTotal = 15 - ddiDigits.length;
  return Math.max(1, Math.min(15, maxByTotal));
};
const splitPhoneParts = (value, fallbackDdi) => {
  const digits = sanitizeNumber(value);
  const fallbackDigits = sanitizeNumber(fallbackDdi);

  if (!digits) {
    return { ddi: fallbackDigits, number: "" };
  }

  // Se o nカmero tem pelo menos 2 dヴgitos, considera os 2 primeiros como DDI
  if (digits.length >= 2) {
    return {
      ddi: digits.slice(0, 2),
      number: digits.slice(2),
    };
  }

  return { ddi: fallbackDigits, number: digits };
};
const isSamePhone = (originalDigits, newDigits, fallbackDdi) => {
  const fallbackDigits = sanitizeNumber(fallbackDdi);

  if (!originalDigits && !newDigits) return true;
  if (originalDigits === newDigits) return true;
  if (
    fallbackDigits &&
    originalDigits &&
    `${fallbackDigits}${originalDigits}` === newDigits
  ) {
    return true;
  }
  if (
    fallbackDigits &&
    newDigits &&
    `${fallbackDigits}${newDigits}` === originalDigits
  ) {
    return true;
  }
  return false;
};

const ModalEdicaoGerente = ({
  open,
  gerente,
  usuarioNome = "",
  saving = false,
  success = false,
  errorMessage = "",
  onClose,
  onConfirm,
}) => {
  const celularOriginal = useMemo(
    () => gerente?.celular || gerente?.telefone || "",
    [gerente]
  );
  const [novoNome, setNovoNome] = useState("");
  const [gerenteSnapshot, setGerenteSnapshot] = useState(null);
  const [novoCelularDdi, setNovoCelularDdi] = useState("");
  const [novoCelularNumero, setNovoCelularNumero] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (open && gerente) {
      setGerenteSnapshot(gerente);
      const celularParts = splitPhoneParts(celularOriginal, defaultDdi);
      setNovoNome(gerente.nome || "");
      setNovoCelularDdi(celularParts.ddi || defaultDdi);
      setNovoCelularNumero(celularParts.number || "");
      setValidationErrors({});
    }
  }, [gerente, open, celularOriginal]);

  const nomeAlterado =
    gerente && (novoNome || "").trim() !== (gerente.nome || "");
  const celularOriginalSanitizado = sanitizeNumber(celularOriginal);
  const novoCelularDdiDigits = sanitizeNumber(novoCelularDdi);
  const novoCelularNumeroDigits = sanitizeNumber(novoCelularNumero);
  const celularNumeroMaxDigits = getPhoneMaxDigits(novoCelularDdiDigits);
  const celularNovoFinal = buildPhoneValue(
    novoCelularDdiDigits,
    novoCelularNumeroDigits,
    defaultDdi
  );
  const novoCelularSanitizado = sanitizeNumber(celularNovoFinal);

  const celularLengthError = getPhoneLengthError(
    novoCelularDdiDigits,
    novoCelularNumeroDigits
  );
  const celularAlterado =
    Boolean(novoCelularNumeroDigits) &&
    !isSamePhone(celularOriginalSanitizado, novoCelularSanitizado, defaultDdi);
  const celularLengthOk = !celularAlterado || !celularLengthError;
  const houveAlteracao = nomeAlterado || celularAlterado;

  const handleConfirm = async () => {
    if (!houveAlteracao) return;

    const errors = {};
    const nomeFinal = (novoNome || "").trim();

    if (!nomeFinal) {
      errors.novoNome = "Informe o nome do gerente";
    }

    if (novoCelularNumeroDigits) {
      if (novoCelularDdi && !novoCelularDdiDigits) {
        errors.novoCelularDdi = "Informe o DDI";
      }

      if (!novoCelularNumeroDigits) {
        errors.novoCelularNumero = "Informe o numero do celular";
      } else {
        const lengthError = getPhoneLengthError(
          novoCelularDdiDigits,
          novoCelularNumeroDigits
        );
        if (lengthError) {
          errors.novoCelularNumero = lengthError;
        }
      }
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0 || !onConfirm) return;

    const novoCelularFinal = celularAlterado
      ? celularNovoFinal || celularOriginalSanitizado
      : celularOriginalSanitizado;

    await onConfirm({
      novoNome: nomeFinal,
      novoCelular: novoCelularFinal,
      celularAtual: celularOriginalSanitizado,
    });
  };

  const gerenteHeader = gerenteSnapshot || gerente;
  const headerText =
    usuarioNome && gerenteHeader?.nome
      ? `Editar dados do gerente ${gerenteHeader.nome}`
      : "Editar dados do gerente";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {headerText}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Nome do gerente"
            fullWidth
            value={novoNome}
            onChange={(e) => setNovoNome(e.target.value)}
            error={Boolean(validationErrors.novoNome)}
            helperText={validationErrors.novoNome}
            disabled={saving}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="DDI"
              value={novoCelularDdi}
              onChange={(e) => {
                const sanitizedDdi = sanitizeDdiInput(e.target.value);
                const sanitizedDigits = sanitizeNumber(sanitizedDdi);
                setNovoCelularDdi(sanitizedDdi);
                setNovoCelularNumero((current) =>
                  sanitizePhoneInput(current, getPhoneMaxDigits(sanitizedDigits))
                );
              }}
              error={Boolean(validationErrors.novoCelularDdi)}
              helperText={validationErrors.novoCelularDdi}
              disabled={saving}
              inputProps={{ inputMode: "numeric", maxLength: 3 }}
              sx={{ maxWidth: 140 }}
            />
            <TextField
              label="celular"
              fullWidth
              value={novoCelularNumero}
              onChange={(e) =>
                setNovoCelularNumero(
                  sanitizePhoneInput(e.target.value, celularNumeroMaxDigits)
                )
              }
              error={Boolean(validationErrors.novoCelularNumero)}
              helperText={validationErrors.novoCelularNumero}
              disabled={saving}
              inputProps={{ inputMode: "numeric", maxLength: celularNumeroMaxDigits }}
            />
          </Stack>

          {errorMessage && (
            <Alert severity="error" variant="outlined">
              {errorMessage}
            </Alert>
          )}

          {success && (
            <Alert severity="success" variant="outlined">
              Dados do gerente atualizados com sucesso.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Box
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Button onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {saving && <CircularProgress size={20} thickness={5} />}
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={saving || !houveAlteracao || !celularLengthOk}
            >
              Enviar Alterações
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ModalEdicaoGerente;
